// React
import React, { useState, useEffect, Fragment } from 'react'
import PropTypes from 'prop-types'

// Components
import BDurations from 'ui/blocks/Durations'
import BPicker from 'ui/blocks/Picker'
import Popover from 'ui/components/Popover'
import PortalWrapper from 'ui/components/PortalWrapper'
import H2 from 'ui/elements/H2'
import P from 'ui/elements/P'

// Hooks
import { usePicker } from 'ui/components/Picker/hooks'

// Shared
import { useTranslation } from 'shared/utils/useTranslation'

const DurationButton = (props) => (
  <BDurations.Duration
    isActive={props.data.value === props.field.value}
    onClick={() => props.onSelect(props.data.value)}
    type="button"
  >
    {props.data.label}
  </BDurations.Duration>
)

DurationButton.propTypes = {
  data: PropTypes.object,
  field: PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date), PropTypes.object]),
    onChange: PropTypes.func,
    onBlur: PropTypes.func,
    onInvalid: PropTypes.func,
    /** Custom validation function should return invalid key or true
     * ex. validate = (date) => 'HH'
     */
    validate: PropTypes.func
  }),
  onSelect: PropTypes.func
}

const DurationsPopoverContent = ({ maxHeight = '420px', ...props }) => {
  const { t } = useTranslation('user')

  const handleSelect = (value) => {
    props.onSelect(value)
  }

  if (!props.options.length) {
    return (
      <BDurations data-tid="Durations popover" maxHeight={maxHeight}>
        <BDurations.Cleanstate>
          <H2>{t(props.translate ? 'user.store.no_time_available_header' : 'common.time_picker.no_options_header')}</H2>
          <P>{t(props.translate ? 'user.store.no_time_available_text' : 'common.time_picker.no_options_body')}</P>
        </BDurations.Cleanstate>
      </BDurations>
    )
  }

  return (
    <BDurations data-tid="Durations popover" maxHeight={maxHeight}>
      <BDurations.Durations>
        {props.options.map((duration) => (
          <DurationButton key={duration.value} {...props} data={duration} onSelect={handleSelect} />
        ))}
      </BDurations.Durations>
    </BDurations>
  )
}

DurationsPopoverContent.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.any,
      value: PropTypes.any
    })
  ),
  onSelect: PropTypes.func,
  handleClose: PropTypes.func,
  maxHeight: PropTypes.string,
  translate: PropTypes.bool
}

export const Durations = (props) => {
  const [active, setActive] = useState(false)

  const picker = usePicker()

  useEffect(() => {
    if (!props.usePortal) return

    const isActive = picker.selection === 'duration'

    setActive(isActive)

    props.onTrigger?.()

    if (isActive) {
      props.onOpen?.()
    } else {
      props.onHide?.()
    }
  }, [picker.selection, props.usePortal])

  const value = props.options.find((option) => option.value === props.field.value)?.label

  const handleSelect = (value) => {
    props.field.onChange(value)
    setActive(false)
  }

  const handleClick = (e) => {
    e.preventDefault()

    if (active) {
      picker.setSelection(null)

      props.onHide?.()
    } else {
      picker.setSelection('duration')

      props.onOpen?.()
    }

    setActive((prev) => !prev)

    props.onTrigger?.()
  }

  const handleTrigger = () => {
    setActive(true)

    props.onTrigger?.()
  }

  const handleHide = () => {
    setActive(false)

    props.onHide?.()
  }

  const handleOpen = () => {
    picker.setSelection('duration')

    props.onOpen?.()
  }

  const handleSetActive = (e) => {
    e.preventDefault()

    setActive(true)
  }

  if (props.usePortal) {
    return (
      <Fragment>
        <BPicker.Button
          type="button"
          onClick={handleClick}
          active={active}
          empty={!props.field.value}
          data-tid={props['data-tid']}
        >
          {value || props.placeholder}
        </BPicker.Button>
        <PortalWrapper open={active} containerRef={props.portalRef}>
          <DurationsPopoverContent onSelect={handleSelect} {...props} />
        </PortalWrapper>
      </Fragment>
    )
  }

  return (
    <Popover
      innerRef={props.innerRef}
      content={<DurationsPopoverContent onSelect={handleSelect} {...props} />}
      method="click"
      direction="bottom"
      offset={15}
      onTrigger={handleTrigger}
      onHide={handleHide}
      onOpen={handleOpen}
      {...(props.popoverOptions || {})}
    >
      <BPicker.Button
        type="button"
        active={active}
        empty={!props.field.value}
        onClick={handleSetActive}
        data-tid={props['data-tid']}
      >
        {value || props.placeholder}
      </BPicker.Button>
    </Popover>
  )
}

Durations.propTypes = {
  'options': PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.any,
      value: PropTypes.string
    })
  ),
  'field': PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date), PropTypes.object]),
    onChange: PropTypes.func,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,
    onInvalid: PropTypes.func,
    /** Custom validation function should return invalid key or true
     * ex. validate = (date) => 'HH'
     */
    validate: PropTypes.func
  }),
  'popoverOptions': PropTypes.object,
  'placeholder': PropTypes.any,
  'onOpen': PropTypes.func,
  'onTrigger': PropTypes.func,
  'onHide': PropTypes.func,
  'onSelectionChange': PropTypes.func,
  'inline': PropTypes.bool,
  'data-tid': PropTypes.string,
  'innerRef': PropTypes.func,
  'portalRef': PropTypes.any,
  'usePortal': PropTypes.bool
}
